import React from "react";

import Sidebar from "./Sidebar/Sidebar";
import { LineAndStopSection } from "./Sections/LineAndStopSection";
import { ZoneSection } from "./Sections/ZoneSection";
import { CityZoneSection } from "./Sections/CityZoneSection";
import { ParkingSection } from "./Sections/ParkingSpotsSection";
import { TicketAgentsSection } from "./Sections/TicketAgentsSection";
import { TicketMachinesSection } from "./Sections/TicketMachinesSection";
import MeasureSidebar from "./Sections/MeasureSection";
import TimetableSection from "../TransitOverlay/Sidebar/TimePeriod";

import { useLineSearch } from "../../Filter/useLineSearch";
import { useStopSearch } from "../../Filter/useStopSearch";
import { useSetAtom } from "jotai";
import { recentItemsAtom } from "../../../atoms/recent";
import { selectedTpidAtom } from "../../../atoms";

import MapSearchInput, { type SearchOption } from "./MapSearchInput";
import { Button, CompactButton, Heading, Icon } from "@ogt/ui";

import type { LineDetails, Zone, Stop } from "../../../types";

import {
  useTrafficOverviewSearch,
  type TrafficOverviewSearchOption,
} from "../../../hooks/useTrafficOverviewSearch";

import {
  useTimetablePeriods,
  useActivePeriod,
  useSelectedPeriod,
} from "../../../api/useTimetablePeriods";

export type SidebarIdStrict =
  | "stopAndLines"
  | "zones"
  | "cityZones"
  | "extras"
  | "measure"
  | "timetable";

export type SidebarActivity = Partial<Record<SidebarIdStrict, number>>;

interface TransitOverlayProps {
  selectedLines: LineDetails[];
  activeLineId: string | null;
  onAddAndActivateLine: (line: string | LineDetails) => void;
  onLineRemove: (lineId: string) => void;
  showLiveByLine: Set<string>;
  onToggleShowLiveForLine: (lineId: string, checked: boolean) => void;
  onAddStopFromLine: (s: Stop) => void;
  unavailableLineIds?: Set<string>;
  liveStateByLine?: Record<string, { emptyHits: number; unavailable: boolean }>;

  allStops: Stop[];
  selectedStops: Stop[];
  activeStopId: string | null;
  onStopSelect: (stop: Stop) => void;
  onStopRemove: (stopId: string) => void;
  setActiveStopId: (stopId: string) => void;

  onAddLineById: (lineId: string) => void;
  onClearStops: () => void;
  onClearActive: () => void;
  activeStopLocation?: Stop | null;
  activeStopLocations?: Stop[];
  showStopLocationsButton?: boolean;
  onShowStopLocations?: () => void;
  onHideStopLocations?: () => void;

  selectedCityZones: Zone[];
  activeCityZoneId: string | null;
  onAddAndActivateCityZone: (zone: Zone) => void;
  onCityZoneRemove: (zoneId: string) => void;
  setActiveCityZoneId: (zoneId: string | null) => void;

  ticketAgentsEnabled: boolean;
  onTicketAgentsEnabledChange: (next: boolean) => void;
  setRequestedTicketAgentName: (next: string | null) => void;

  ticketMachinesEnabled: boolean;
  onTicketMachinesEnabledChange: (next: boolean) => void;
  setRequestedTicketMachineName: (next: string | null) => void;

  measuring: boolean;
  onToggleMeasuring: () => void;
  totalDistanceKm: number;
  measurePoints: google.maps.LatLngLiteral[];
  onClearMeasurement: () => void;

  openSidebarId: SidebarIdStrict | null;
  onSidebarToggle: (id: SidebarIdStrict) => void;

  sidebarActivity?: SidebarActivity;

  onAddLinesToList: (lines: LineDetails[]) => void;
  onClearAll: () => void;
}

const stopKeyOf = (id: string | number) => String(id).split("-")[0];

type StopSearchOptionWithData = SearchOption & {
  data?: Stop[];
  fuzzyFallback?: boolean;
  rawValue?: string;
};

type RankedStopSearchOption = StopSearchOptionWithData & {
  score: number;
  index: number;
};

type CombinedSearchOption = SearchOption & {
  kind: "stop" | "line" | "lineNetwork";
  data?: Stop[];
  networkOption?: TrafficOverviewSearchOption;
  fuzzyFallback?: boolean;
  rawValue?: string;
};

function normalizeSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/\s+/g, " ");
}

function shouldPrioritizeLineNetworks(query: string) {
  const normalized = normalizeSearch(query);

  return normalized === "all" || normalized.startsWith("all ");
}

function distance(a: string, b: string) {
  const d = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i++) d[i][0] = i;
  for (let j = 0; j <= b.length; j++) d[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[a.length][b.length];
}

function stripStopName(name: string) {
  return String(name ?? "")
    .replace(/\s*[\(\[]?[A-ZÅÄÖ][\)\]]?$/, "")
    .trim();
}

function getStopOptionRoot(option: StopSearchOptionWithData): string | null {
  const dataId = option.data?.[0]?.id;

  if (dataId != null) {
    return stopKeyOf(dataId);
  }

  const candidate = option.rawValue ?? option.id ?? option.value;

  const cleaned = String(candidate ?? "")
    .replace(/^stop:/, "")
    .trim();

  if (!cleaned) return null;

  const idPart = cleaned.split(/\s+/)[0];

  return idPart ? stopKeyOf(idPart) : null;
}

function mergeStopData(first?: Stop[], second?: Stop[]): Stop[] | undefined {
  const stops = [...(first ?? []), ...(second ?? [])];

  if (stops.length === 0) return undefined;

  return Array.from(
    new Map(stops.map((stop) => [String(stop.id), stop])).values()
  );
}

function dedupeStopOptions(
  options: StopSearchOptionWithData[]
): StopSearchOptionWithData[] {
  const result = new Map<string, StopSearchOptionWithData>();

  options.forEach((option, index) => {
    const root = getStopOptionRoot(option);
    const key = root ? `stop:${root}` : `unknown:${index}`;

    const existing = result.get(key);

    if (!existing) {
      result.set(key, option);
      return;
    }

    const preferred =
      existing.fuzzyFallback && !option.fuzzyFallback ? option : existing;

    const secondary = preferred === existing ? option : existing;

    result.set(key, {
      ...preferred,
      rawValue: preferred.rawValue ?? secondary.rawValue,
      data: mergeStopData(preferred.data, secondary.data),
      fuzzyFallback:
        Boolean(preferred.fuzzyFallback) && Boolean(secondary.fuzzyFallback),
    });
  });

  return Array.from(result.values());
}

function getStopScore(label: string, query: string) {
  const q = normalizeSearch(query);
  const text = normalizeSearch(label);

  if (q.length < 3 || !text) return null;

  const words = text.split(/[\s,()/-]+/).filter(Boolean);

  if (text.includes(q)) {
    return text.startsWith(q) ? 0 : 10;
  }

  if (q.length >= 4 && q.length <= 8) {
    let best = Number.POSITIVE_INFINITY;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      if (word.length < q.length - 1) {
        continue;
      }

      const candidate = word.slice(0, q.length);
      const penalty = i === 0 ? 0 : 20 + i * 5;

      best = Math.min(best, distance(q, candidate) + penalty);
    }

    const maxDistance = q.length <= 7 ? 2 : 3;

    if (best <= maxDistance + 20) {
      return 20 + best;
    }
  }

  for (let len = Math.min(q.length - 1, 6); len >= 3; len--) {
    const prefix = q.slice(0, len);

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      if (!word.startsWith(prefix)) {
        continue;
      }

      if (text.startsWith(prefix)) {
        return 100 + q.length - len;
      }

      if (i === 0) {
        return 130 + q.length - len;
      }

      return 200 + i * 20 + q.length - len;
    }
  }

  return null;
}

function toSearchableStopValue(option: SearchOption, query: string) {
  return [option.value || option.id, option.label, query]
    .filter(Boolean)
    .join(" ");
}

export default function TransitOverlay({
  selectedLines,
  activeLineId,
  onAddAndActivateLine,
  onLineRemove,
  showLiveByLine,
  onToggleShowLiveForLine,
  onAddStopFromLine,
  unavailableLineIds,
  onAddLinesToList,
  liveStateByLine,

  allStops,
  selectedStops,
  activeStopId,
  onStopSelect,
  onStopRemove,
  setActiveStopId,
  onAddLineById,
  onClearStops,
  onClearActive,
  activeStopLocation,
  activeStopLocations = [],
  showStopLocationsButton = false,
  onShowStopLocations,
  onHideStopLocations,

  selectedCityZones,
  activeCityZoneId,
  onAddAndActivateCityZone,
  onCityZoneRemove,
  setActiveCityZoneId,

  ticketAgentsEnabled,
  onTicketAgentsEnabledChange,
  ticketMachinesEnabled,
  onTicketMachinesEnabledChange,
  setRequestedTicketAgentName,
  setRequestedTicketMachineName,

  measuring,
  onToggleMeasuring,
  onClearMeasurement,
  measurePoints,
  totalDistanceKm,

  openSidebarId,
  onSidebarToggle,
  sidebarActivity = {},
}: TransitOverlayProps) {
  const setRecents = useSetAtom(recentItemsAtom);
  const setSelectedTpid = useSetAtom(selectedTpidAtom);

  const [combinedSearchQuery, setCombinedSearchQuery] = React.useState("");
  const [mobileMoreOpen, setMobileMoreOpen] = React.useState(false);
  const mobileMoreCount =
    (sidebarActivity.extras ?? 0) + (sidebarActivity.measure ?? 0);

  const handleMobileMoreToggle = React.useCallback(() => {
    setMobileMoreOpen((current) => {
      const next = !current;

      if (next && openSidebarId) {
        onSidebarToggle(openSidebarId);
      }

      return next;
    });
  }, [openSidebarId, onSidebarToggle]);

  React.useEffect(() => {
    if (openSidebarId) {
      setMobileMoreOpen(false);
    }
  }, [openSidebarId]);

  const markSeen = React.useCallback(
    (kind: "stop" | "line", id: string) => {
      setRecents((prev) => {
        const without = prev.filter((x) => !(x.kind === kind && x.id === id));

        return [
          {
            kind,
            id,
            ts: Date.now(),
          },
          ...without,
        ].slice(0, 300);
      });
    },
    [setRecents]
  );

  const {
    options: lineOptions,
    handleInputChange: onLineInputChange,
    handleSelect: onLineSelectRaw,
    loading: lineLoading,
  } = useLineSearch(onAddAndActivateLine);

  const {
    options: stopOptions,
    handleInputChange: onStopInputChange,
    loading: stopLoading,
  } = useStopSearch(allStops, (stop) => {
    markSeen("stop", stopKeyOf(stop.id));
    onStopSelect(stop);
  });

  const {
    options: trafficOverviewOptions,
    loading: trafficOverviewLoading,
    fetchDetailsForOption,
  } = useTrafficOverviewSearch(combinedSearchQuery);

  const fuzzyStopOptions = React.useMemo<StopSearchOptionWithData[]>(() => {
    const query = combinedSearchQuery.trim();

    if (query.length < 4) return [];

    const existingRoots = new Set(
      (stopOptions as StopSearchOptionWithData[])
        .map(getStopOptionRoot)
        .filter((root): root is string => root !== null)
    );

    const grouped = new Map<
      string,
      {
        stops: Stop[];
        index: number;
      }
    >();

    allStops.forEach((stop, index) => {
      const root = stopKeyOf(stop.id);
      const current = grouped.get(root);

      if (current) {
        current.stops.push(stop);
      } else {
        grouped.set(root, {
          stops: [stop],
          index,
        });
      }
    });

    const hits: RankedStopSearchOption[] = [];

    for (const [root, group] of grouped) {
      if (existingRoots.has(root)) {
        continue;
      }

      const first = group.stops[0];

      if (!first) continue;

      const label = stripStopName(first.name || root);

      const searchText = [
        label,
        root,
        (first as any).cityName,
        (first as any).taxeZoneName,
      ]
        .filter(Boolean)
        .join(" ");

      const score = getStopScore(searchText, query);

      if (score == null) continue;

      hits.push({
        id: root,
        label,
        value: `${root} ${label} ${query}`,
        rawValue: root,
        type: "Hållplats",
        group: "Hållplatser",
        data: group.stops,
        fuzzyFallback: true,
        score,
        index: group.index,
      });
    }

    return hits
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score;
        }

        if (a.index !== b.index) {
          return a.index - b.index;
        }

        return a.label.localeCompare(b.label, "sv");
      })
      .slice(0, 10)
      .map(({ score, index, ...option }) => option);
  }, [allStops, stopOptions, combinedSearchQuery]);

  const combinedSearchOptions = React.useMemo<CombinedSearchOption[]>(() => {
    const stopSearchOptions = dedupeStopOptions([
      ...(stopOptions as StopSearchOptionWithData[]),
      ...fuzzyStopOptions,
    ]);

    const stops = stopSearchOptions.map((option): CombinedSearchOption => {
      const rawValue = option.rawValue ?? option.value ?? option.id;

      return {
        ...option,
        id: `stop:${option.id}`,
        value: toSearchableStopValue(option, combinedSearchQuery),
        rawValue,
        type: "Hållplats",
        group: "Hållplatser",
        kind: "stop",
        data: option.data,
        fuzzyFallback: option.fuzzyFallback,
      };
    });

    const lines = lineOptions.map(
      (option): CombinedSearchOption => ({
        ...option,
        id: `line:${option.id}`,
        value: option.value,
        type: "Linje",
        group: "Linjer",
        kind: "line",
      })
    );

    const lineNetworks = trafficOverviewOptions.map(
      (option): CombinedSearchOption => ({
        ...option,
        id: `network:${option.id}`,
        value: option.value,
        type: "Linjenät",
        group: "Linjenät",
        kind: "lineNetwork",
        networkOption: option,
      })
    );

    if (shouldPrioritizeLineNetworks(combinedSearchQuery)) {
      return [...lineNetworks, ...stops, ...lines];
    }

    return [...stops, ...lines, ...lineNetworks];
  }, [
    stopOptions,
    fuzzyStopOptions,
    lineOptions,
    trafficOverviewOptions,
    combinedSearchQuery,
  ]);

  const handleCombinedSearchInputChange = React.useCallback(
    (value: string) => {
      setCombinedSearchQuery(value);
      onStopInputChange(value);
      onLineInputChange(value);
    },
    [onStopInputChange, onLineInputChange]
  );

  const clearCombinedSearch = React.useCallback(() => {
    setCombinedSearchQuery("");
    onStopInputChange("");
    onLineInputChange("");
  }, [onStopInputChange, onLineInputChange]);

  const handleCombinedSearchSelect = React.useCallback(
    async (option: SearchOption) => {
      const picked = option as CombinedSearchOption;

      if (picked.kind === "stop") {
        const first = picked.data?.[0];

        if (first) {
          markSeen("stop", stopKeyOf(first.id));
          onStopSelect(first);
          clearCombinedSearch();
          return;
        }

        const rawId = String(picked.id).replace(/^stop:/, "");
        const rawRootId = stopKeyOf(rawId);

        const fallbackStop = allStops.find(
          (stop) =>
            String(stop.id) === rawId || stopKeyOf(stop.id) === rawRootId
        );

        if (fallbackStop) {
          markSeen("stop", stopKeyOf(fallbackStop.id));
          onStopSelect(fallbackStop);
        }

        clearCombinedSearch();
        return;
      }

      if (picked.kind === "line") {
        markSeen("line", String(picked.value));

        await onLineSelectRaw({
          ...picked,
          id: String(picked.id).replace(/^line:/, ""),
        });

        clearCombinedSearch();
        return;
      }

      if (picked.kind === "lineNetwork" && picked.networkOption) {
        const details = await fetchDetailsForOption(picked.networkOption);

        onAddLinesToList(details);

        for (const line of details) {
          markSeen("line", String(line.id));
        }

        clearCombinedSearch();
      }
    },
    [
      allStops,
      markSeen,
      onStopSelect,
      onLineSelectRaw,
      fetchDetailsForOption,
      onAddLinesToList,
      clearCombinedSearch,
    ]
  );

  const { periods, loading: periodsLoading } = useTimetablePeriods();

  const { loading, error, label, fromStr, toStr } = useActivePeriod();

  const {
    selectedId: selectedPeriodId,
    setSelectedId: setSelectedPeriodId,
    selected: selectedPeriod,
  } = useSelectedPeriod(periods, label);

  React.useEffect(() => {
    setSelectedTpid(selectedPeriod?.id ?? null);
  }, [selectedPeriod?.id, setSelectedTpid]);

  const periodYear = label?.match(/\b\d{4}\b/)?.[0] ?? "";

  const periodName = label
    ? label.replace(periodYear, "").trim()
    : loading
    ? "Hämtar..."
    : "Okänd";

  return (
    <div className="sidebar relative flex h-full w-full shrink-0 overflow-visible border-r border-r-[var(--semantic-color-border-primary)] bg-[var(--semantic-color-bg-primary)]">
      <div className="sidebar-button-group-wrapper relative flex h-full w-full">
        <nav
          className={[
            "!flex",
            "fixed bottom-[0] left-0 right-0 z-50",
            "h-[90px] w-full flex-row items-stretch gap-1",
            "bg-[var(--menu-color-bg)] px-2 py-2",

            "md:static md:h-full md:w-[112px]",
            "md:shrink-0 md:flex-col md:px-3 md:py-5",
          ].join(" ")}
        >
          {" "}
          <Sidebar
            title="Hållplatser och linjer"
            id="stopAndLines"
            isOpen={openSidebarId === "stopAndLines"}
            onToggle={() => onSidebarToggle("stopAndLines")}
            leadingIconName="busfront"
            activeCount={sidebarActivity.stopAndLines ?? 0}
          >
            <div className="flex flex-col">
              <div className="p-4 pb-1 pt-0">
                <Heading>Hållplatser och linjer</Heading>

                <MapSearchInput
                  label="Sök hållplats eller linje"
                  value={combinedSearchQuery}
                  onInputChange={handleCombinedSearchInputChange}
                  onSelect={handleCombinedSearchSelect}
                  options={combinedSearchOptions}
                  placeholder=""
                  loading={stopLoading || lineLoading || trafficOverviewLoading}
                />
              </div>

              <LineAndStopSection
                selectedLines={selectedLines}
                activeLineId={activeLineId}
                onAddAndActivateLine={onAddAndActivateLine}
                onLineRemove={onLineRemove}
                selectedStops={selectedStops}
                onRemoveStopGlobal={onStopRemove}
                activeStopId={activeStopId}
                activeStopLocation={activeStopLocation}
                activeStopLocations={activeStopLocations}
                showStopLocationsButton={showStopLocationsButton}
                onShowStopLocations={onShowStopLocations}
                onHideStopLocations={onHideStopLocations}
                onStopRemove={onStopRemove}
                onClearStops={onClearStops}
                visibleNartrafikAreaCount={sidebarActivity.zones ?? 0}
                onShowNartrafikAreas={() => onSidebarToggle("zones")}
                onClearActive={onClearActive}
                setActiveStopId={setActiveStopId}
                unavailableLineIds={unavailableLineIds}
                liveStateByLine={liveStateByLine}
                onStopSelect={(stop) => {
                  markSeen("stop", stopKeyOf(stop.id));

                  onStopSelect(stop);
                }}
                showLiveByLine={showLiveByLine}
                onToggleShowLiveForLine={onToggleShowLiveForLine}
                onAddLineById={onAddLineById}
                onAddStopFromLine={onAddStopFromLine}
                allStops={allStops}
              />
            </div>
          </Sidebar>
          <Sidebar
            title="Närtrafik"
            id="zones"
            isOpen={openSidebarId === "zones"}
            onToggle={() => onSidebarToggle("zones")}
            leadingIconName="car"
            activeCount={sidebarActivity.zones ?? 0}
          >
            <ZoneSection />
          </Sidebar>
          <Sidebar
            title="Stadszoner"
            id="cityZones"
            isOpen={openSidebarId === "cityZones"}
            onToggle={() => onSidebarToggle("cityZones")}
            leadingIconName="zone:land-plot"
            activeCount={sidebarActivity.cityZones ?? 0}
          >
            <CityZoneSection
              selectedCityZones={selectedCityZones}
              activeCityZoneId={activeCityZoneId}
              onAddAndActivateCityZone={onAddAndActivateCityZone}
              onCityZoneRemove={onCityZoneRemove}
              setActiveCityZoneId={setActiveCityZoneId}
            />
          </Sidebar>
          <Sidebar
            title="Ombud och parkeringar"
            id="extras"
            isOpen={openSidebarId === "extras"}
            onToggle={() => onSidebarToggle("extras")}
            leadingIconName="pin"
            activeCount={sidebarActivity.extras ?? 0}
            mobileTriggerHidden
          >
            <div className="p-4 pb-6 pt-0">
              <Heading>Ombud och parkeringar</Heading>

              <p>
                Här kan du se våra försäljningsställen och var det finns
                pendlarparkeringar i länet.
              </p>

              <ul className="!list-none mb-4 !pl-0">
                <li className="mb-1">
                  <ParkingSection />
                </li>

                <li className="mb-1">
                  <TicketAgentsSection
                    enabled={ticketAgentsEnabled}
                    onEnabledChange={onTicketAgentsEnabledChange}
                    onShowAll={() => {
                      setRequestedTicketAgentName(null);
                    }}
                  />
                </li>

                <li className="mb-1">
                  <TicketMachinesSection
                    enabled={ticketMachinesEnabled}
                    onEnabledChange={onTicketMachinesEnabledChange}
                    onShowAll={() => {
                      setRequestedTicketMachineName(null);
                    }}
                  />
                </li>
              </ul>
            </div>
          </Sidebar>
          <Sidebar
            title="Mät avstånd"
            id="measure"
            isOpen={openSidebarId === "measure"}
            onToggle={() => onSidebarToggle("measure")}
            leadingIconName="ruler"
            activeCount={sidebarActivity.measure ?? 0}
            mobileTriggerHidden
          >
            <MeasureSidebar
              open={openSidebarId === "measure"}
              onToggle={() => onSidebarToggle("measure")}
              measuring={measuring}
              onToggleMeasuring={onToggleMeasuring}
              onClearMeasurement={onClearMeasurement}
              measurePoints={measurePoints}
              totalDistanceKm={totalDistanceKm}
            />
          </Sidebar>
          <hr className="hidden md:block md:my-2 md:border-t-[var(--semantic-color-divider-secondary)]" />
          <Sidebar
            title="Tidtabell"
            id="timetable"
            isOpen={openSidebarId === "timetable"}
            onToggle={() => onSidebarToggle("timetable")}
            className="md:mt-auto"
            mobileTriggerHidden
            renderTrigger={({ isOpen, onToggle, panelId }) => (
              <CompactButton
                type="button"
                onClick={onToggle}
                variant="secondary"
                showIcon
                direction="vertical"
                iconPosition="left"
                iconSize={22}
                size="small"
                activated={isOpen}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="w-full !py-2"
              >
                <div className="mb-0 text-sm">Tidtabell:</div>

                <div className="text-xl font-bold leading-tight">
                  {periodYear || "—"}
                </div>

                <div className="text-sm">{periodName}</div>
              </CompactButton>
            )}
          >
            <div className="p-4 pb-6">
              <TimetableSection
                advancedMode
                periodsLoading={periodsLoading}
                periods={periods}
                loading={loading}
                error={Boolean(error)}
                label={label}
                fromStr={fromStr}
                toStr={toStr}
                selectedPeriodId={selectedPeriodId}
                setSelectedPeriodId={setSelectedPeriodId}
              />
            </div>
          </Sidebar>
          <div className="relative min-w-0 max-w-18 ml-auto flex-1 md:hidden">
            <CompactButton
              type="button"
              variant="secondary"
              direction="vertical"
              size="small"
              onClick={handleMobileMoreToggle}
              aria-expanded={mobileMoreOpen}
              aria-controls="mobile-more-menu"
              className={[
                "w-full",
                "h-18",
                "p-2!",
                "text-xs!",
                "justify-between!",
                "md:h-auto",
                "md:p-2!",
                "md:text-sm!",
                "md:justify-center!",
                mobileMoreOpen &&
                  "group hover:no-underline! !text-white bg-[var(--button-secondary-color-bg-active)] hover:text-[var(--button-color-text-inverted)]!",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span
                aria-hidden="true"
                className="flex min-h-[22px] items-center text-xl font-bold leading-[12px]"
              >
                •••
              </span>

              <span className="min-h-[30px] text-xs md:min-h-auto">Mer</span>
            </CompactButton>

            {mobileMoreCount > 0 && (
              <span
                aria-hidden="true"
                className={[
                  "pointer-events-none",
                  "absolute right-1 top-1 z-20",
                  "flex min-h-5 min-w-5 items-center justify-center",
                  "rounded-full px-1",
                  "bg-[var(--button-primary-color-bg-active)] text-white",
                  "text-[11px] font-bold leading-none border border-2",
                ].join(" ")}
              >
                {mobileMoreCount > 99 ? "99+" : mobileMoreCount}
              </span>
            )}

            {mobileMoreOpen && (
              <div
                id="mobile-more-menu"
                className={[
                  "fixed bottom-[calc(90px)] w-22 h-auto flex flex-col right-0 z-50",
                  " bg-[var(--menu-color-bg)]",
                  "p-2",
                  "md:hidden",
                ].join(" ")}
              >
                <div className="flex flex-col gap-2">
                  <div className="relative">
                    <CompactButton
                      type="button"
                      variant="secondary"
                      direction="vertical"
                      size="small"
                      onClick={() => {
                        setMobileMoreOpen(false);
                        onSidebarToggle("extras");
                      }}
                      className="w-full h-auto p-2! text-xs! justify-between! md:h-auto md:p-2! md:text-sm! md:justify-center!"
                    >
                      <span className="flex min-h-[22px] items-center text-xl">
                        <Icon name="pin" size={20} />
                      </span>

                      <span className="min-h-[30px] text-center text-xs md:min-h-auto">
                        Ombud och parkeringar
                      </span>
                    </CompactButton>

                    {(sidebarActivity.extras ?? 0) > 0 && (
                      <span
                        aria-hidden="true"
                        className="
                        pointer-events-none
                        absolute right-1 top-1 z-20
                        flex min-h-5 min-w-5
                        items-center justify-center
                        rounded-full px-1
                        bg-[var(--button-compact-primary-color-bg-active)]
                        text-[11px] font-bold leading-none text-white border border-2
                        "
                      >
                        {sidebarActivity.extras}
                      </span>
                    )}
                  </div>

                  <div className="relative">
                    <CompactButton
                      type="button"
                      variant="secondary"
                      direction="vertical"
                      size="small"
                      onClick={() => {
                        setMobileMoreOpen(false);
                        onSidebarToggle("measure");
                      }}
                      className="w-full h-auto p-2! text-xs! justify-between! md:h-auto md:p-2! md:text-sm! md:justify-center!"
                    >
                      <span className="flex min-h-[22px] items-center text-xl">
                        <Icon name="ruler" size={20} />
                      </span>

                      <span className="min-h-[30px] text-center text-xs md:min-h-auto">
                        Mät avstånd
                      </span>
                    </CompactButton>

                    {(sidebarActivity.measure ?? 0) > 0 && (
                      <span
                        aria-hidden="true"
                        className=" pointer-events-none absolute right-1 top-1 z-20 flex min-h-5 min-w-5 items-center justify-center rounded-full px-1 bg-[var(--button-compact-primary-color-bg-active)] text-[11px] font-bold leading-none text-white"
                      >
                        {sidebarActivity.measure}
                      </span>
                    )}
                  </div>

                  <div className="relative">
                    <CompactButton
                      type="button"
                      variant="secondary"
                      direction="vertical"
                      size="small"
                      onClick={() => {
                        setMobileMoreOpen(false);
                        onSidebarToggle("timetable");
                      }}
                      className="w-full h-auto p-2! text-xs! justify-between! md:h-auto md:p-2! md:text-sm! md:justify-center! "
                    >
                      <span className="flex min-h-[22px] items-center text-xl">
                        <Icon name="route" size={20} />
                      </span>

                      <span className="min-h-[30px] text-center text-xs md:min-h-auto">
                        Tidtabell
                        {periodYear && (
                          <span className="block text-[10px] font-normal opacity-70">
                            {periodYear}
                          </span>
                        )}
                      </span>
                    </CompactButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
